import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";

import type { TimelineEntry, WorkLogEntry } from "../../session-logic";

import type { Translator } from "~/i18n";

import { toolCallLogoUrl } from "../../morrowAgentDisplay";
import { localizeToolCallDescription, localizeWorkEntryHeading } from "./toolCallDescriptionI18n";
import { describeToolCall, resolveWorkEntryStep } from "./workEntryStep";

export const WORKING_OPERATION_CYCLE_MS = 2200;

export type WorkingOperationGlyph =
  | "sparkles"
  | "globe"
  | "wrench"
  | "eye"
  | "terminal"
  | "bot"
  | "pen";

export type WorkingOperation = {
  readonly id: string;
  readonly label: string;
  readonly logoUrl: string | null;
  readonly glyph: WorkingOperationGlyph;
  readonly toolName?: string;
};

export function nextWorkingOperationIndex(current: number, length: number): number {
  if (length <= 1) {
    return 0;
  }
  return (current + 1) % length;
}

export function clampWorkingOperationIndex(current: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return ((current % length) + length) % length;
}

/** Work rows after the latest user message — the live turn's background steps. */
export function collectLatestTurnWorkEntries(
  entries: ReadonlyArray<TimelineEntry>,
): WorkLogEntry[] {
  const collected: WorkLogEntry[] = [];
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (!entry) {
      continue;
    }
    if (entry.kind === "message" && entry.message.role === "user") {
      break;
    }
    if (entry.kind === "work") {
      collected.push(entry.entry);
    }
  }
  return collected.reverse();
}

function isLiveWorkEntry(entry: WorkLogEntry): boolean {
  if (entry.agentSpawn) {
    return false;
  }
  if (entry.toolLifecycleStatus === "inProgress") {
    return true;
  }
  if (entry.tone !== "thinking") {
    return false;
  }
  return (
    entry.toolLifecycleStatus !== "completed" &&
    entry.toolLifecycleStatus !== "failed" &&
    entry.toolLifecycleStatus !== "stopped"
  );
}

function glyphForWorkEntry(entry: WorkLogEntry): WorkingOperationGlyph {
  if (entry.tone === "thinking") {
    return "sparkles";
  }
  if (entry.requestKind === "file-read" || entry.itemType === "image_view") {
    return "eye";
  }
  if (entry.requestKind === "file-change" || entry.itemType === "file_change") {
    return "pen";
  }
  if (entry.requestKind === "command" || entry.itemType === "command_execution") {
    return "terminal";
  }
  if (entry.itemType === "web_search") {
    return "globe";
  }
  if (entry.taskId) {
    return "bot";
  }
  return "wrench";
}

function operationFromWorkEntry(entry: WorkLogEntry, t?: Translator): WorkingOperation {
  const step = resolveWorkEntryStep(entry);
  const label = t ? localizeWorkEntryHeading(entry, step.heading, t) : step.heading;
  return {
    id: entry.id,
    label,
    logoUrl: step.integration
      ? (toolCallLogoUrl(step.integration.slug) ??
        `https://logos.composio.dev/api/${step.integration.slug}`)
      : null,
    glyph: glyphForWorkEntry(entry),
  };
}

function operationFromToolCall(
  call: MorrowAgent["toolCalls"][number],
  t?: Translator,
): WorkingOperation {
  const raw = describeToolCall(call.toolName);
  const desc = t ? localizeToolCallDescription(raw, call.toolName, t) : raw;
  return {
    id: call.id,
    label: desc.appName ? `${desc.appName} · ${desc.action}` : desc.action,
    logoUrl: toolCallLogoUrl(call.toolName),
    glyph: "wrench",
    toolName: call.toolName,
  };
}

/**
 * Live background steps for the in-chat working row. Prefers currently running
 * Morrow tool calls (newest first), then in-progress work-log rows, then a
 * single fallback so the row never goes blank while a turn is in flight.
 */
export function deriveWorkingOperations(input: {
  morrowAgents: ReadonlyArray<MorrowAgent>;
  workEntries: ReadonlyArray<WorkLogEntry>;
  fallbackLabel: string;
  t?: Translator;
}): WorkingOperation[] {
  const { t } = input;
  const runningTools: WorkingOperation[] = [];
  const runningAgents: WorkingOperation[] = [];
  const seenToolNames = new Set<string>();

  for (const agent of input.morrowAgents) {
    if (agent.status !== "running") {
      continue;
    }
    const liveCalls = agent.toolCalls.filter((call) => call.status === "running");
    if (liveCalls.length === 0) {
      runningAgents.push({
        id: `agent:${agent.agentId}`,
        label: agent.task?.trim() || input.fallbackLabel,
        logoUrl: null,
        glyph: "bot",
      });
      continue;
    }
    const newestFirst = [...liveCalls].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    for (const call of newestFirst) {
      runningTools.push(operationFromToolCall(call, t));
      seenToolNames.add(call.toolName.trim().toLowerCase());
    }
  }

  if (runningTools.length > 0) {
    return runningTools;
  }
  if (runningAgents.length > 0) {
    return runningAgents;
  }

  const liveWork = input.workEntries
    .filter(isLiveWorkEntry)
    .filter((entry) => {
      const toolName = entry.toolName?.trim().toLowerCase();
      return !toolName || !seenToolNames.has(toolName);
    })
    .map((entry) => operationFromWorkEntry(entry, t));

  if (liveWork.length > 0) {
    return liveWork.toReversed();
  }

  return [
    {
      id: "working-fallback",
      label: input.fallbackLabel,
      logoUrl: null,
      glyph: "sparkles",
    },
  ];
}
